import { APP } from 'config';
import { converters as tableConverters } from 'store/table';
import {
  IBaseCompany,
  IBaseParentCompany,
  ICompany,
  ICompanyContact,
  IListCompany,
  IParentCompany,
  IParentCompanyWithStats,
} from 'types/income';
import { upper } from 'utils/general';
import { isDefined } from 'utils/isDefined';
import { convertUploadedFile } from 'store/files';
import { loaders } from './companies.loaders';
import {
  BaseCompany,
  BaseParentCompany,
  ListCompany,
  ParentCompany,
  ParentCompanyWithStats,
  Company,
  CompanyContact,
} from './companies.types';

export const convertBaseCompany = (company: IBaseCompany): BaseCompany => {
  return {
    id: company.id,
    name: company.name,
    code: company.code,
    status: company.status,
  };
};

export const convertBaseCompanies = (
  companies: IBaseCompany[],
): BaseCompany[] => {
  return companies.map(convertBaseCompany);
};

const convertListCompany = (company: IListCompany): ListCompany => {
  return {
    ...convertBaseCompany(company),
    employeeCount: Number(company.employeeCount),
    departmentCount: Number(company.departmentCount),
  };
};

export const convertCompany = (company: ICompany): Company => {
  return {
    ...convertBaseCompany(company),
    country: company.country,
    currency: company.currency,
    logoId: company.logoId ?? '',
    additionalInfo: company.additionalInfo ?? '',
    manager: company.manager
      ? { id: company.manager.id, name: company.manager.name }
      : null,
    billingInfo: company.settings.billingInfo ?? '',
    supportedLanguages: company.supportedLanguages,
    creationDate: company.creationDate,
    activationDate: company.activationDate ?? null,
    deactivationDate: company.deactivationDate ?? null,
    departments:
      company.departments?.map((department) => ({
        id: department.id,
        name: department.name,
        hidden: department.hidden,
      })) ?? [],
    branding: {
      colors: [
        company.settings.branding?.primaryColor,
        company.settings.branding?.secondaryColor,
      ].filter(isDefined),
    },
    settings: {
      sourceOfRecognitionPoints: company.settings.externalRecognitionProvider
        ? 'externalProvider'
        : 'melp',
      canGiveRecognition: company.settings.canGiveRecognition,
      hidePointBalanceMobile: company.settings.hidePointBalanceMobile,
      accountantEmails: company.settings.accountantEmails ?? [],
      accountantLanguage:
        company.settings.accountantLanguage || upper(APP.locales.default),
    },
  };
};

const convertCompanies = (companies: IListCompany[]): ListCompany[] => {
  return companies.map(convertListCompany);
};

const convertCompanyContact = (contact: ICompanyContact): CompanyContact => {
  return {
    id: contact.id,
    phone: contact.phone ?? '',
    firstName: contact.firstName ?? '',
    lastName: contact.lastName ?? '',
    fullName: [contact.firstName, contact.lastName].filter(Boolean).join(' '),
    email: contact.email ?? '',
  };
};

const convertCompanyContacts = (
  contacts: ICompanyContact[],
): CompanyContact[] => {
  return contacts.map(convertCompanyContact);
};

export const convertBaseParentCompany = (
  client: IBaseParentCompany,
): BaseParentCompany => {
  return {
    id: client.id,
    name: client.name,
    country: client.country,
    internalComment: client.internalComment ?? '',
    status: client.status,
    activeEmployees: client.activeEmployees ?? 0,
    activeMobileUsers: client.activeMobileUsers ?? 0,
    inactiveEmployees: client.inactiveEmployees ?? 0,
  };
};

export const convertParentCompany = (client: IParentCompany): ParentCompany => {
  return {
    ...convertBaseParentCompany(client),
    code: client.code,
    defaultCurrency: client.defaultCurrency,
    creationDate: client.creationDate,
    deactivationDate: client.deactivationDate,
    supportedLanguages: client.supportedLanguages,
    features: client.features,
    companies: client.companies?.map(convertCompany) ?? [],
    logo: client.logo ? convertUploadedFile(client.logo) : null,
    branding: {
      colors: [
        client.settings.branding?.primaryColor,
        client.settings.branding?.secondaryColor,
      ].filter(isDefined),
    },
    billingInfo: client.settings.billingInfo ?? '',
    settings: {
      defaultLanguage: client.settings.defaultLanguage,
      isPersonalCodeRequiredForLogin:
        client.settings.isPersonalCodeRequiredForLogin,
      isEmailVisibleInternally: client.settings.isEmailVisibleInternally,
      allowChangeProfilePicture: client.settings.allowChangeProfilePicture,
      discountPlan: client.settings.discountPlan,
      depositManagementMethod: client.settings.depositManagementMethod,
      useExternalId: client.settings.useExternalId,
      accountantEmails: client.settings.accountantEmails ?? [],
      accountantLanguage:
        client.settings.accountantLanguage || upper(APP.locales.default),
      merchandiseContactPersonName:
        client.settings.merchandiseContactPersonName ?? '',
      merchandiseContactPersonPhone:
        client.settings.merchandiseContactPersonPhone ?? '',
      merchandiseContactPersonEmail:
        client.settings.merchandiseContactPersonEmail ?? '',
      companyExpenseEnabled: client.settings.company_expense_enabled ?? false,
      companyExpenseEmail: client.settings.company_expense_email ?? '',
      expenseCompensationAskIban:
        client.settings.expenseCompensationAskIban ?? false,
      authProvider: client.settings.authProvider,
      authProviderName: client.settings.authProviderName ?? '',
      sendEmployeeWelcomeMessage: client.settings.sendEmployeeWelcomeMessage,
      conversationalAgentTranslations:
        client.settings.conversationalAgentTranslations,
      conversationalAgentId: client.settings.conversationalAgentId ?? '',
      ragId: client.settings.ragId ?? '',
      ragDescription: client.settings.ragDescription ?? '',
      agentResponseTone: client.settings.agentResponseTone,
      agentShowCitations: client.settings.agentShowCitations,
      expenseCompensationBenefitCombination:
        client.settings.expenseCompensationBenefitCombination,
      ipWhitelistEnabled: client.settings.ipWhitelistEnabled ?? false,
      ipWhitelist: client.settings.ipWhitelist ?? [],
      sessionTimeoutEnabled: client.settings.sessionTimeoutEnabled ?? false,
      sessionTimeoutInMinutes: client.settings.sessionTimeoutInMinutes ?? NaN,
    },
  };
};

const convertParentCompaniesWithStats = (
  companies: IParentCompanyWithStats[],
): ParentCompanyWithStats[] => {
  return companies.map(convertBaseParentCompany);
};

export const converters = {
  getCompanies: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getCompanies>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertCompanies(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getCompany: ({ data }: Awaited<ReturnType<typeof loaders.getCompany>>) => {
    return convertCompany(data);
  },
  getCompanyContacts: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getCompanyContacts>>) => {
    return convertCompanyContacts(data);
  },
  getParentCompanies: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getParentCompanies>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertParentCompaniesWithStats(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
};
